import type { Category, EmploymentType, Profession, PublisherType, Region, TenderType } from './taxonomy';

/**
 * The wire shapes of `/api/tenders/*`.
 *
 * Hand-written rather than vendored, unlike `taxonomy.ts`, because these describe PROJECTIONS the
 * serving layer chose — not the pipeline's document. A list row is deliberately a subset (the
 * backend's `LIST_PROJECTION`); vendoring the ETL's `Posting` would describe a shape this app never
 * receives, and would carry `searchText`, the one field that must never cross the wire.
 */

export type Kind = 'tender' | 'job';
export type PostingStatus = 'open' | 'closed' | 'delisted';
/**
 * `relevance` ranks by search score and is only valid alongside a text query — the server rejects
 * it without one. It exists because the sort control is no longer disabled during a text search:
 * the ETL's Atlas index used to type the deadline as a `date` over an ISO STRING, so Atlas could
 * sort on it not at all and the text path silently came back ranked whatever the user picked.
 */
export type Sort = 'relevance' | 'closing' | 'newest';

export interface Labeled {
  value: string;
  label: string;
}

export interface PostingDates {
  published: string | null;
  lastSubmissionDate: string | null;
  clarification: string | null;
  lastUpdate: string | null;
}

export interface PostingValue {
  amount: number;
  currency: string;
  kind: string | null;
}

/** One row in the results list — mirrors `LIST_PROJECTION` in huaca/src/products/tenders/service.ts. */
export interface PostingListItem {
  slug: string;
  siteId: string;
  kind: Kind;
  title: string;
  category: Category | null;
  categoryLabel: string | null;
  region: Region | null;
  /** FREE TEXT, not an enum — live data contains at least one English leak (`haifa`). */
  city: string | null;
  status: PostingStatus;
  tenderType: TenderType | null;
  employmentType: EmploymentType | null;
  professions: Labeled[];
  securityClearanceRequired: boolean | null;
  dates: PostingDates;
  /**
   * DATES ONLY on a list row — the backend projects `'meetings.date': 1`.
   *
   * The card's gate strip says when the bidders' meeting is, never where, so `location` and
   * `registrationLink` deliberately do not travel with a list page. `PostingDetail` widens this
   * back to the full `PostingMeeting`, which is legal precisely because the detail shape is a
   * superset of what the list sends.
   */
  meetings: { date: string | null }[];
  value: PostingValue | null;
  source: { publisher: string; publisherType: PublisherType; detailLink: string | null };
  lifecycle: { firstSeen: string };
}

/** An attachment, as metadata only — the file itself is never proxied, `url` points at the source portal. */
export interface PostingDocument {
  url: string;
  name: string;
  mime: string;
  pageCount: number;
  textStatus: string;
}

export interface PostingMeeting {
  date: string | null;
  location: string | null;
  registrationLink: string | null;
}

/** The detail view: everything the pipeline stores except `searchText`. */
export interface PostingDetail extends PostingListItem {
  details: Record<string, string | null>;
  tags: string[];
  contact: { name: string | null; email: string | null; phone: string | null };
  meetings: PostingMeeting[];
  documents: PostingDocument[];
  source: {
    adapter: string;
    publisher: string;
    publisherType: PublisherType;
    listUrl: string;
    detailLink: string | null;
    fetchedAt: string;
  };
  lifecycle: { firstSeen: string; lastSeen: string; delistedAt: string | null; reconcileStatus: string };
}

/** Ascending or descending. Both sortable columns offer both. */
export const SORT_DIRECTIONS = ['asc', 'desc'] as const;
export type SortDirection = (typeof SORT_DIRECTIONS)[number];

/**
 * Each sort key's natural direction, mirroring `NATURAL_DIRECTION` on the server.
 *
 * Mirrored rather than fetched for the same reason `effectiveSort` mirrors the sort default: a
 * header has to show which way it is ordering before the first response lands, and a control that
 * shows one direction while another is being fetched is worse than one that shows nothing.
 */
export const NATURAL_DIRECTION: Record<'closing' | 'newest', SortDirection> = {
  closing: 'asc',
  newest: 'desc',
};

export interface PostingsPage {
  items: PostingListItem[];
  /** 1-based, echoed by the server so the client reads the page it got rather than the one it asked for. */
  page: number;
  /** Echoed too — "page 3 of 7" needs it, and it is a server default the client never sends. */
  pageSize: number;
  hasMore: boolean;
  /**
   * The size of the whole result set. Still nullable: on the text path it comes from Atlas
   * `$searchMeta`, which can decline to report one, and `null` means "unknown" rather than zero.
   */
  total: number | null;
  /**
   * The ordering that ACTUALLY ran — now always the one requested, since `sort` is honoured on both
   * query paths. Still read rather than assumed: it costs nothing, and it is what would surface a
   * server-side override instead of the UI quietly mislabelling the list.
   */
  ordering: Sort;
  /** The direction that ran, resolved by the server when the client named none. */
  direction: SortDirection;
}

export type FacetDimension =
  | 'kind'
  | 'category'
  | 'region'
  | 'tenderType'
  | 'employmentType'
  | 'profession'
  | 'publisherType';

/**
 * Deadline windows, in CHRONOLOGICAL order — which is the order the server returns them in and the
 * order the sidebar reads. The selection itself is stored alphabetically to keep the URL canonical;
 * the two orders are deliberately different things and neither derives from the other.
 *
 * Not from the vendored taxonomy: a window is a range computed against the clock by the serving
 * layer, not a value the pipeline ever writes to a document.
 */
export const CLOSING_WINDOWS = ['now', 'week', 'month', 'later', 'none'] as const;
export type ClosingWindow = (typeof CLOSING_WINDOWS)[number];

/** The seven stored dimensions plus the computed one. Mirrors `FacetKey` on the server. */
export type FacetKey = FacetDimension | 'closing';

/** Every value of a closed taxonomy, in taxonomy order — including the ones that matched nothing. */
export type FacetCounts = Record<FacetKey, { value: string; count: number }[]>;

export type { Category, EmploymentType, Profession, PublisherType, Region, TenderType };
